import { SearchIcon } from "lucide-react";
import { ReactNode } from "react";

import { useShortcut } from "@/utils/use-shortcut";

import {
  CommandDialog,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "./base/command";
import { ShortcutHint } from "./shortcut-hint";
import { Spinner } from "./spinner";

/**
 * Cap on rendered results, shared by every palette. A palette lists rows
 * straight from a list that can hold thousands — building that many
 * CommandItems on every keystroke is what makes one feel slow. A search that
 * still matches more than this is a search worth narrowing, so the heading says
 * how many were left out rather than silently truncating.
 */
export const MAX_SEARCH_RESULTS = 50;

/**
 * One part of a result's detail line. Numbers are accepted so callers can pass
 * raw values; falsy entries are dropped, which lets `cond && value` guards read
 * inline.
 */
export type SearchResultDetail = string | number | false | null | undefined;

/**
 * One result row: name on the left, a short accent on the right, and a line of
 * qualifying detail beneath. Every palette in the app renders through this, so
 * results look the same whichever table they came from — which is also why
 * `subtitle` is worth filling in even when it's only loosely identifying.
 */
export function SearchPaletteItem({
  value,
  title,
  subtitle,
  badge,
  onSelect,
}: {
  /** cmdk's item key — must be unique within the list. */
  value: string;
  title: string;
  /** Detail parts, joined with "·". Falsy entries are dropped. */
  subtitle?: SearchResultDetail[];
  badge?: string;
  onSelect: () => void;
}) {
  const parts =
    subtitle?.filter((part) => !!part).map((part) => String(part)) ?? [];
  return (
    <CommandItem
      value={value}
      onSelect={onSelect}
      className="flex-col items-start gap-0.5 cursor-pointer"
    >
      <div className="flex items-center justify-between w-full">
        <span className="font-medium">{title}</span>
        {badge && (
          <span className="text-xs font-bold text-brand-primary ml-2 whitespace-nowrap">
            {badge}
          </span>
        )}
      </div>
      {/* Rendered even when empty so every row keeps the same height. */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground min-h-4">
        {parts.map((part, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span>·</span>}
            <span>{part}</span>
          </span>
        ))}
      </div>
    </CommandItem>
  );
}

/**
 * ⌘K command palette: a header trigger plus the dialog it opens, with results
 * supplied by the caller as `CommandItem` children.
 *
 * Only one palette may be mounted per page — the ⌘K listener is global while
 * mounted, so two would both fire.
 */
export function SearchPalette({
  open,
  onOpenChange,
  query,
  onQueryChange,
  triggerLabel,
  inputPlaceholder,
  title,
  description,
  loading,
  emptyMessage,
  children,
}: {
  open: boolean;
  /** Called with `true` by the trigger and ⌘K, with `false` on Escape/dismiss. */
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  /** Text on the header trigger button, e.g. "Search patients...". */
  triggerLabel: string;
  inputPlaceholder: string;
  /** Accessible dialog title and description (visually hidden). */
  title: string;
  description: string;
  loading?: boolean;
  emptyMessage: string;
  children: ReactNode;
}) {
  useShortcut("k", () => onOpenChange(true));

  return (
    <>
      {/* One size for every palette in the app — the trigger should look
          identical whichever page or table it belongs to.

          Below `nav` (the breakpoint where the app switches to its mobile
          layout) it collapses to a square icon button: a 320px-wide fake input
          would crowd out the rest of the header row, and there's no keyboard to
          hint ⌘K at. `aria-label` carries the name the hidden text would
          otherwise give it. */}
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        aria-keyshortcuts="Meta+K Control+K"
        aria-label={triggerLabel}
        className="flex items-center justify-center shrink-0 size-9 text-sm text-muted-foreground bg-background border border-input hover:border-ring hover:bg-muted rounded-lg transition-all cursor-pointer nav:size-auto nav:justify-start nav:gap-3 nav:w-64 nav:px-4 nav:py-2 lg:w-[25vw] lg:min-w-80"
      >
        <SearchIcon className="size-5 text-muted-foreground" />
        <span className="hidden nav:inline">{triggerLabel}</span>
        <ShortcutHint className="ml-auto hidden nav:inline">⌘K</ShortcutHint>
      </button>

      <CommandDialog
        // The caller owns matching (so it can fold diacritics and prefix-match
        // ids); cmdk's own scorer would fight it.
        shouldFilter={false}
        showCloseButton={false}
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        className="w-150"
      >
        <CommandInput
          placeholder={inputPlaceholder}
          value={query}
          onValueChange={onQueryChange}
        />
        <CommandList className="h-100">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Spinner />
            </div>
          ) : (
            <>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              {children}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
